import { useUserAuthState, useUserRoutes } from '@cosuno/authentication-frontend';
import { CompanySettingsTabs } from '@cosuno/routes';
import { queryStringToObject } from '@cosuno/utils';
import { useCallback, useMemo } from 'react';
import { useMatch } from 'react-router-dom';

export const useAgentSignupRedirectRoute = () => {
  const routes = useUserRoutes();
  const { isUserAuthenticated } = useUserAuthState();

  const matchesReverseMarketplace = useMatch({
    path: routes.agentReverseMarketplace(),
    end: false,
  });
  const matchesReverseMarketplaceBidPackage = useMatch({
    path: routes.reverseMarketplaceBidPackage(),
    end: false,
  });
  const matchesReverseMarketplaceSuggestion = useMatch({
    path: routes.agentReverseMarketplaceSuggestion(),
    end: false,
  });
  const matchesAgentPremium = useMatch({ path: routes.premium(), end: false });
  const matchesLegacyAgentPremium = useMatch({
    path: routes.company({ tab: CompanySettingsTabs.premium }),
    end: false,
  });
  const matchesShowTask = useMatch({ path: routes.showTask(), end: false });

  const shouldRedirectToSignup =
    !isUserAuthenticated &&
    (matchesReverseMarketplace !== null ||
      matchesReverseMarketplaceBidPackage !== null ||
      matchesReverseMarketplaceSuggestion !== null ||
      matchesAgentPremium !== null ||
      matchesLegacyAgentPremium !== null ||
      matchesShowTask !== null);

  const getFullRoute = useCallback(() => {
    if (matchesShowTask) {
      const search = queryStringToObject<{
        sentEmailId?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
      }>(window.location.search);

      // if query does not contain sentEmailId, redirect to default login page
      if (!search.sentEmailId) {
        return undefined;
      }

      // if query contains sentEmailUrl, redirect to custom login page
      return routes.taskLogin({
        ...search,
        taskId: matchesShowTask.params.id,
      });
    }

    return routes.agentUnsolicitedSignup({ redirect: window.location.pathname });
  }, [routes, matchesShowTask]);

  return useMemo(() => {
    if (!shouldRedirectToSignup) {
      return undefined;
    }

    const fullRoute = getFullRoute();

    if (!fullRoute) {
      return undefined;
    }

    const [pathname, search] = fullRoute.split('?');

    return {
      pathname,
      search,
      fullRoute,
    };
  }, [shouldRedirectToSignup, getFullRoute]);
};
